import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product SKU.
 * This component listens for variant update events and updates the SKU display accordingly.
 * It handles SKU updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} skuContainer - The container element for the SKU
 * @property {HTMLElement} sku - The span element that displays the SKU text
 * @property {HTMLButtonElement} [copyButton] - Optional copy-to-clipboard button
 *
 * @extends {Component<Refs>}
 */
class ProductSkuComponent extends Component {
	requiredRefs = ['skuContainer', 'sku'];

	connectedCallback() {
		super.connectedCallback();
		const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
		if (!target) return;
		target.addEventListener(ThemeEvents.variantUpdate, this.updateSku);
		this.refs.copyButton?.addEventListener('click', this.copySku);
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		const target = this.closest('[id*="ProductInformation-"], [id*="QuickAdd-"], product-card');
		if (!target) return;
		target.removeEventListener(ThemeEvents.variantUpdate, this.updateSku);
		this.refs.copyButton?.removeEventListener('click', this.copySku);
	}

	copySku = async (event) => {
		event?.preventDefault();
		const text = this.refs.sku?.textContent?.trim();
		if (!text) return;

		let success = false;
		try {
			if (window.isSecureContext && navigator.clipboard?.writeText) {
				await navigator.clipboard.writeText(text);
				success = true;
			}
		} catch {
			success = false;
		}

		if (!success) {
			const textarea = document.createElement('textarea');
			textarea.value = text;
			textarea.setAttribute('readonly', '');
			textarea.style.cssText = 'position:fixed;top:0;left:0;opacity:0;pointer-events:none;';
			document.body.appendChild(textarea);
			textarea.select();
			textarea.setSelectionRange(0, text.length);
			try {
				success = document.execCommand('copy');
			} catch {
				success = false;
			}
			textarea.remove();
		}

		if (!success) return;

		const button = this.refs.copyButton;
		if (!button) return;
		const defaultIcon = button.querySelector('.sku-copy__icon--default');
		const successIcon = button.querySelector('.sku-copy__icon--success');
		defaultIcon?.setAttribute('hidden', '');
		successIcon?.removeAttribute('hidden');
		clearTimeout(this._copyResetTimer);
		this._copyResetTimer = setTimeout(() => {
			defaultIcon?.removeAttribute('hidden');
			successIcon?.setAttribute('hidden', '');
		}, 1500);
	};

	/**
	 * Updates the SKU.
	 * @param {VariantUpdateEvent} event - The variant update event.
	 */
	updateSku = (event) => {
		if (event.detail.data.newProduct) {
			this.dataset.productId = event.detail.data.newProduct.id;
		}

		if (
			event.target instanceof HTMLElement &&
			event.target.dataset.productId !== this.dataset.productId
		) {
			return;
		}

		// Use the variant data from the event
		// The variant is in event.detail.resource
		if (event.detail.resource) {
			const variantSku = event.detail.resource.sku || '';

			if (variantSku) {
				// Show the component and update the SKU
				this.style.display = 'block';
				this.refs.sku.textContent = variantSku;
			} else {
				// Hide the entire component when SKU is empty
				this.style.display = 'none';
				this.refs.sku.textContent = '';
			}
		}
	};
}

if (!customElements.get('product-sku-component')) {
	customElements.define('product-sku-component', ProductSkuComponent);
}
